import { z } from 'zod';
import { assetKindSchema } from './enums.js';
/**
 * Contrats de la CORBEILLE (soft-delete) réversible des médias (Lot 1).
 * Source unique de vérité front + back.
 *
 * « Mettre à la poubelle » = suppression LOGIQUE en base (`assets.deleted_at`),
 * RÉVERSIBLE, qui NE TOUCHE PAS le fichier disque (fonctionne en lecture seule). Un
 * asset supprimé disparaît de toutes les vues (grille, dossiers, tags, compteurs,
 * dédup, EXIF, tagging IA, API externe v1, détail visionneuse) SAUF la corbeille,
 * jusqu'à sa restauration (ou sa purge définitive = Lot 2).
 *
 * ⚠ PIÈGE ZOD v4 (boot crash) : ces schémas de REQUÊTE passent par
 * `z.toJSONSchema(schema, { target: 'draft-7' })` au démarrage (enregistrement des
 * routes Fastify). Un `.transform()` PERSONNALISÉ y LÈVE → l'API plante en boucle.
 * On n'en met AUCUN ici (uniquement `.min`/`.max`/`.optional`/`z.coerce.number` —
 * représentables). Toute normalisation « vide → null » etc. se fait dans le HANDLER.
 * Garde anti-régression : `route-json-schema.test.ts` rejoue `z.toJSONSchema` sur eux.
 */
// ---------------------------------------------------------------------------
// Mise à la corbeille (POST /api/libraries/:id/assets/soft-delete)
// ---------------------------------------------------------------------------
/** Corps de la mise à la corbeille : une sélection non vide d'ids d'assets. */
export const softDeleteRequestSchema = z.object({
    assetIds: z.array(z.uuid()).min(1),
});
/** Résultat : nombre d'assets EFFECTIVEMENT mis à la corbeille (déjà supprimés ignorés). */
export const softDeleteResultSchema = z.object({
    deleted: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Restauration (POST /api/libraries/:id/assets/restore)
// ---------------------------------------------------------------------------
/** Corps de la restauration : une sélection non vide d'ids d'assets à sortir de la corbeille. */
export const restoreRequestSchema = z.object({
    assetIds: z.array(z.uuid()).min(1),
});
/** Résultat : nombre d'assets EFFECTIVEMENT restaurés (non-supprimés ignorés). */
export const restoreResultSchema = z.object({
    restored: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Listing de la corbeille (GET /api/libraries/:id/trash)
// ---------------------------------------------------------------------------
/** Bornes de pagination du listing de la corbeille (protège la DB + le rendu). */
export const TRASH_MAX_LIMIT = 200;
export const TRASH_DEFAULT_LIMIT = 50;
/**
 * Query de `GET /api/libraries/:id/trash`. `limit` borné (1..200, défaut 50),
 * `offset` ≥ 0. Coercition depuis la querystring via `z.coerce.number()` (AJV
 * coerce, Zod re-valide — comme `fileOpErrorsQuerySchema`).
 */
export const trashQuerySchema = z.object({
    limit: z.coerce.number().int().positive().max(TRASH_MAX_LIMIT).default(TRASH_DEFAULT_LIMIT),
    offset: z.coerce.number().int().nonnegative().default(0),
});
/**
 * Ligne de corbeille : champs nécessaires au rendu d'une carte + de sa vignette.
 * `deletedAt` non nullable (par définition présent pour un asset en corbeille).
 */
export const trashAssetSchema = z.object({
    id: z.uuid(),
    relPath: z.string(),
    filename: z.string(),
    kind: assetKindSchema,
    mime: z.string(),
    width: z.number().int().nonnegative().nullable(),
    height: z.number().int().nonnegative().nullable(),
    sizeBytes: z.number().int().nonnegative(),
    takenAt: z.string().datetime({ offset: true }).nullable(),
    deletedAt: z.string().datetime({ offset: true }),
});
/** Page de corbeille (`{ assets, total }`) — `total` dimensionne la pagination. */
export const trashListResultSchema = z.object({
    assets: z.array(trashAssetSchema),
    total: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Purge DÉFINITIVE (POST /api/libraries/:id/trash/purge) — Lot 2
// ---------------------------------------------------------------------------
/**
 * Corps de `POST /api/libraries/:id/trash/purge`. Purge DÉFINITIVEMENT (irréversible)
 * une sélection d'assets EN CORBEILLE : `unlink` du fichier sur /library PUIS suppression
 * de la ligne DB + ses lignes filles + ses vignettes.
 *
 * DEUX modes exclusifs (au moins un requis, sinon 400 via `.refine`) :
 *  - `all: true`             → purge TOUS les assets en corbeille de la bibliothèque.
 *  - `assetIds: [...]` (≥ 1) → purge cette sélection (seuls ceux en corbeille sont purgés).
 *
 * La purge est une ÉCRITURE DISQUE (`unlink`) : la route exige l'accès écriture ET
 * `LIBRARY_WRITABLE` (409 sinon). `dryRun` : aperçu (compte sans rien supprimer).
 *
 * ⚠ PIÈGE ZOD v4 (boot crash) : PAS de `.transform()` (LÈVE dans `z.toJSONSchema`) —
 * seuls `.array`/`.boolean`/`.optional`/`.refine` (représentables). Garde :
 * `route-json-schema.test.ts`.
 */
export const trashPurgeRequestSchema = z
    .object({
    assetIds: z.array(z.uuid()).optional(),
    all: z.boolean().optional(),
    dryRun: z.boolean().optional().default(false),
})
    .refine((v) => v.all === true || (v.assetIds !== undefined && v.assetIds.length > 0), {
    message: 'Fournir all:true ou une liste assetIds non vide',
});
/**
 * Ventilation d'un job `trash_purge`. Chaque asset ciblé tombe dans une catégorie :
 *  - `purged`       : ligne DB (+ lignes filles + vignettes) supprimée (le fichier a été
 *                     unlinké, ou était déjà absent — voir `filesMissing`).
 *  - `filesDeleted` : fichiers RÉELLEMENT unlinkés du disque (sous-ensemble de `purged`).
 *  - `filesMissing` : fichiers déjà absents du disque (ENOENT) — la ligne est purgée
 *                     quand même (la DB rejoint le disque).
 *  - `failed`       : chemin refusé (traversée) ou suppression DB en échec — non purgé.
 */
export const trashPurgeResultSchema = z.object({
    purged: z.number().int().nonnegative(),
    filesDeleted: z.number().int().nonnegative(),
    filesMissing: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});
//# sourceMappingURL=trash.js.map